import React, { FC } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { MainComponentFilters } from 'src/generated-types/main-component'
import { FacilityTypes } from 'src/enums'
import { StyledFilteredItemChip } from 'src/screens/localities/maintenance/EquipmentMaintenanceContainer'
import { getStatusName } from '../EquipmentFiltersHelper'

interface FilterChipsProps {
  headerFilters: MainComponentFilters
  clearFilter: (field: string) => void
  localities?: any[]
  serviceStations?: any[]
  storages?: any[]
  updateFilter: (field: string, value: string | number | number[] | string[]) => void
}

const FilterChips: FC<FilterChipsProps> = ({
  headerFilters,
  clearFilter,
  localities,
  serviceStations,
  storages,
  updateFilter,
}) => {
  const { t: translate } = useTranslation(['main-component', 'common'])

  const getLocationName = (value: string) => {
    const [id, type] = value.split('#')
    const facilityId = parseInt(id)
    const facilityType = parseInt(type)

    switch (facilityType) {
      case FacilityTypes.locality:
        return localities?.find(l => l.id === facilityId)?.name || `Locality ${id}`
      case FacilityTypes.serviceStation:
        return serviceStations?.find(s => s.id === facilityId)?.name || `Service Station ${id}`
      case FacilityTypes.storage:
        return storages?.find(s => s.id === facilityId)?.name || `Storage ${id}`
      default:
        return value
    }
  }

  const renderFilterChips = () => {
    const chips: React.ReactNode[] = []

    // Handle where filter
    if (headerFilters.where && Array.isArray(headerFilters.where) && headerFilters.where.length > 0) {
      headerFilters.where.forEach((whereValue, index) => {
        chips.push(
          <StyledFilteredItemChip
            key={`where-${index}-${whereValue}`}
            label={`${translate('where', { ns: 'main-component' })}: ${getLocationName(whereValue)}`}
            onDelete={() => {
              const newWhere = headerFilters.where?.filter(w => w !== whereValue) || []
              if (newWhere.length === 0) {
                clearFilter('where')
              } else {
                updateFilter('where', newWhere)
              }
            }}
          />
        )
      })
    }

    // Handle status filter
    if (headerFilters.status && Array.isArray(headerFilters.status) && headerFilters.status.length > 0) {
      headerFilters.status.forEach((statusValue, index) => {
        chips.push(
          <StyledFilteredItemChip
            key={`status-${index}-${statusValue}`}
            label={`${translate('status', { ns: 'work-assignment-position' })}: ${getStatusName(translate, statusValue)}`}
            onDelete={() => {
              const newStatus = headerFilters.status?.filter(s => s !== statusValue) || []
              if (newStatus.length === 0) {
                clearFilter('status')
              } else {
                updateFilter('status', newStatus)
              }
            }}
          />
        )
      })
    }

    // Handle date filters
    if (headerFilters.dateProduced) {
      chips.push(
        <StyledFilteredItemChip
          key="dateProduced"
          label={`${translate('dateProduced', { ns: 'main-component' })}: ${headerFilters.dateProduced}`}
          onDelete={() => clearFilter('dateProduced')}
        />
      )
    }

    if (headerFilters.lastWashedDate) {
      chips.push(
        <StyledFilteredItemChip
          key="lastWashedDate"
          label={`${translate('lastWashed', { ns: 'common' })}: ${headerFilters.lastWashedDate}`}
          onDelete={() => clearFilter('lastWashedDate')}
        />
      )
    }

    return chips
  }

  const filterChips = renderFilterChips()

  if (filterChips.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {filterChips}
      </Box>
    </Box>
  )
}

export default FilterChips